/* @flow */

import * as React from 'react';
import { useSelector } from 'react-redux';

import { getAuthenticationToken } from 'src/shared/app/authentication/selector/base/appAuthenticationBaseSelectors';

export default function useSocket(
  url: string,
  handleMessage: (Object) => void,
  handleInit?: () => void,
  handleDestroy?: () => void,
) {
  const token = useSelector(getAuthenticationToken);

  let socket;
  let delay = 1000;
  let timeoutId;

  /*
   |----------------------------------------------------------------------------
   | Effects
   |----------------------------------------------------------------------------
   */

  React.useEffect(() => {
    if (url && token) {
      resetSocket();
    }

    return () => {
      closeSocket();
      destroySocket();

      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [url, token]);

  /*
   |----------------------------------------------------------------------------
   | Event Handlers
   |----------------------------------------------------------------------------
   */

  const onMessage = ({ data }: Object) => {
    handleMessage(JSON.parse(String(data)));
  };

  const onClose = ({ wasClean }: Object) => {
    destroySocket();
    if (!wasClean) {
      setTimeout(() => {
        initSocket();
      }, Math.min(delay, 300000));
    }
    delay = delay * 2;
  };

  const onError = () => {};

  /*
   |----------------------------------------------------------------------------
   | Functions
   |----------------------------------------------------------------------------
   */

  const initSocket = () => {
    if (!socket) {
      timeoutId = window.setTimeout(() => {
        socket = new WebSocket(url, ['Token', `${token}`]);

        socket.addEventListener('message', onMessage);
        socket.addEventListener('close', onClose);
        socket.addEventListener('error', onError);

        if (handleInit) {
          handleInit();
        }
        // FIXME: timeout is necessary because of BE issue
      }, 1000);
    }
  };

  const closeSocket = () => {
    if (socket) {
      socket.close();
    }
  };

  const resetSocket = () => {
    closeSocket();
    initSocket();
  };

  const destroySocket = () => {
    if (socket) {
      socket.removeEventListener('message', onMessage);
      socket.removeEventListener('close', onClose);
      socket.removeEventListener('error', onError);

      socket = null;

      if (handleDestroy) {
        handleDestroy();
      }
    }
  };
}
